import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createSlug } from '../../util/urlHelpers';
import { recordLaunchEvent } from '../../util/api';
import {
  LAUNCH_EVENT_CARD_CLICK,
  LAUNCH_STATUS_ARCHIVED,
  LAUNCH_STATUS_COMING_SOON,
  LAUNCH_STATUS_LIVE,
  LAUNCH_TYPE_OPTIONS,
  NEW_THIS_WEEK_DAYS,
  fromEpochDay,
  toEpochDay,
} from '../../config/configWhatsNew';

import { NamedLink, ResponsiveImage } from '../../components';

import css from './LaunchCard.module.css';

// launchType key → label. The vocabulary itself lives in configWhatsNew (it is
// part of the export contract); this just turns it into a lookup so a card and
// a filter chip can never disagree about what 'new_finish' is called.
export const LAUNCH_TYPE_LABELS = LAUNCH_TYPE_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.option]: o.label }),
  {}
);

/**
 * The badge a launch wears, derived from its status and its dates.
 *
 * Returns a key or null — null meaning "this launch is live but neither
 * launching today nor new, so it wears no badge at all". A badge for every
 * card would make the badge meaningless.
 *
 * The returned key is BOTH the translation key suffix and the CSS modifier, so
 * the label is always text plus colour and never colour alone — a
 * colour-only status is invisible to anyone who can't distinguish the hues.
 *
 * @param {Object} publicData the launch's publicData
 * @param {Number} [now] ms timestamp, injectable for tests
 * @returns {'archived'|'comingSoon'|'today'|'new'|null}
 */
export const launchBadgeKey = (publicData, now = Date.now()) => {
  const pd = publicData || {};
  if (pd.launchStatus === LAUNCH_STATUS_ARCHIVED) return 'archived';
  if (pd.launchStatus === LAUNCH_STATUS_COMING_SOON) return 'comingSoon';
  if (pd.launchStatus !== LAUNCH_STATUS_LIVE) return null;

  const day = Number(pd.launchDate);
  if (Number.isFinite(day) && Math.trunc(day) === toEpochDay(now)) return 'today';

  const publishedMs = pd.publishedAtIso ? Date.parse(pd.publishedAtIso) : NaN;
  const isNew =
    Number.isFinite(publishedMs) && publishedMs >= now - NEW_THIS_WEEK_DAYS * 24 * 60 * 60 * 1000;
  return isNew ? 'new' : null;
};

/**
 * Format a launchDate (epoch DAYS, UTC-anchored) for display.
 * Forced to UTC: the stored value is a whole day, and rendering it in the
 * reader's local zone would show the day before for anyone west of Greenwich.
 *
 * @param {Number} epochDay
 * @param {Object} intl
 * @returns {String|null}
 */
export const formatLaunchDate = (epochDay, intl) => {
  const n = Number(epochDay);
  if (epochDay == null || epochDay === '' || !Number.isFinite(n)) return null;
  return intl.formatDate(fromEpochDay(n), {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * A launch on a What's New rail.
 *
 * Deliberately quieter than DesignListingCard: one image, no swipe carousel,
 * no save heart. A rail is a reading surface — the decisions (save, sample,
 * enquire) live on the launch page, and a card full of controls would compete
 * with the twelve cards next to it.
 *
 * Fires a card_click engagement event on navigation, tagged with the rail it
 * was clicked from, so the editorial team can see which rails actually work.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing - the launch listing entity
 * @param {string} [props.sourceSection] - rail key, recorded as metadata.source_section
 * @param {string} [props.className]
 * @param {string} [props.sizes] - <img sizes> hint
 */
const LaunchCard = props => {
  const { listing, sourceSection, className, sizes } = props;
  const intl = useIntl();

  const { title = '', publicData } = listing?.attributes || {};
  const pd = publicData || {};
  const listingId = listing?.id?.uuid;

  const badge = launchBadgeKey(pd);
  const dateLabel = formatLaunchDate(pd.launchDate, intl);
  const typeLabel = LAUNCH_TYPE_LABELS[pd.launchType] || null;
  const image = (listing?.images || [])[0] || null;

  const handleClick = () => {
    if (!listingId) return;
    // Fire-and-forget. The navigation must never wait on (or be cancelled by)
    // an analytics write — the link keeps its default behaviour throughout.
    recordLaunchEvent({
      launchId: listingId,
      event: LAUNCH_EVENT_CARD_CLICK,
      metadata: {
        ...(sourceSection ? { source_section: sourceSection } : {}),
        ...(pd.launchType ? { launch_type: pd.launchType } : {}),
        ...(pd.brandSlug ? { brand_slug: pd.brandSlug } : {}),
      },
    }).catch(() => {});
  };

  // The click listener sits on the WRAPPER, not on the link: NamedLink builds
  // its own <a> props and does not forward onClick, and the click bubbles here
  // anyway. Nothing about the link's behaviour changes — no preventDefault, no
  // waiting on the request — so cmd-click, middle-click and prefetch all still
  // work exactly as they would on a plain link.
  return (
    <div className={classNames(css.card, className)} onClick={handleClick}>
      <NamedLink
        className={css.cardLink}
        name="ListingPage"
        params={{ id: listingId, slug: createSlug(title) }}
      >
        <div className={css.imageWrapper}>
          <ResponsiveImage
            rootClassName={css.image}
            alt={title}
            image={image}
            variants={['listing-card-2x', 'listing-card', 'scaled-medium']}
            sizes={sizes || '(max-width: 767px) 72vw, (max-width: 1023px) 40vw, 22vw'}
          />
          {badge ? (
            <span className={classNames(css.badge, css[`badge_${badge}`])}>
              <FormattedMessage id={`LaunchCard.badge_${badge}`} />
            </span>
          ) : null}
        </div>
        <div className={css.body}>
          {pd.brandName ? <span className={css.brand}>{pd.brandName}</span> : null}
          <h3 className={css.title}>{title}</h3>
          {pd.subtitle ? <p className={css.subtitle}>{pd.subtitle}</p> : null}
          {dateLabel || typeLabel ? (
            <p className={css.meta}>
              {typeLabel ? <span className={css.metaType}>{typeLabel}</span> : null}
              {typeLabel && dateLabel ? <span className={css.metaDot}>·</span> : null}
              {dateLabel ? <span className={css.metaDate}>{dateLabel}</span> : null}
            </p>
          ) : null}
        </div>
      </NamedLink>
    </div>
  );
};

export default LaunchCard;
